// Mobs: the first objects in this world that move because they want to.
//
// Everything else that changes tiles is moved *by* something -- a shove, a
// Momentum cast, a bind link dragging it along, a river it was pushed into.
// A mob steps on its own, toward the caster, every `physics.mob[variant].stepMs`
// of simulated time. That is the entire mechanism; the rest of this file is
// about what it deliberately reuses rather than invents.
//
// **A mob is an ordinary object.** It blocks, it is pushable, it holds
// `properties`, it is drawn from a texture, and it is relocated through
// `casts.ts`'s own `moveObjectTo` -- the same function a Sokoban push and a
// Transfer·Momentum shove call. So a mob that walks onto a pressure pad presses
// it, a mob that walks into a water channel dams it, and a mob that is itself a
// Bind·Momentum source drags whatever follows it, with no line of code here
// mentioning pads, water or binds. If a mob needed its own mover, that would be
// the signal that it is a parallel system rather than a participant in this one.
//
// **Nothing here is a combat statistic.** No health, no damage, no attack, no
// aggro radius -- see `MobDef`'s own comment and `05_Gameplay.md`: an opponent
// is meant to be another physical system to reason about, and this world already
// has real quantities to reason with. What a Grunt does today is walk at you and
// stand in your way. Whatever hurting one eventually means should fall out of
// `properties` and the mechanisms that already read them, the way the floor
// hazards and the thermal band already do for the caster.
//
// **The spells a mob throws are pictures of casts, not casts** (2026-08-11, as
// asked). A mob with a `castMs` emits the same `project-fx` event a real
// `Project` emits and stops there: nothing is drawn from the mob, no property
// moves, and standing in the beam costs exactly nothing. Each of the five spells
// names a real rune expression (see `MobProjectileId`), so the day they become
// real is the day this calls `applyProjectEffect` with those operands -- which is
// why the table below maps each one to the *texture the matching cast already
// uses* rather than to a look invented for mobs. `decay` is the one exception and
// says so; an arrow is not a spell at all and says that too.
//
// **Five kinds, one rule.** Swarmer is a Grunt with a smaller `stepMs`; the five
// Drone Mages are one kind with five `mob.projectile` values; the Apprentice is a
// Drone Mage that picks per cast; the Archer is the only one whose *movement*
// differs, and it differs by reading one more number (`standoff`). Nothing here
// branches on a kind by name at all -- not even the Apprentice, which is simply a
// mob with no `mob.projectile` of its own, a question about the data.
//
// **Where a mob may go is `canObjectEnter`, not `isWalkable`.** A mob is an
// object arriving on a tile, so it obeys the object rule -- which means, among
// other things, that it does not climb: the vine ladder is a route the caster
// earns, and 2026-08-11's "only the caster climbs" fix drew that line precisely
// because everything that is not a deliberate walk was quietly using the walking
// rule. A mob that should climb is a widening of that rule to argue for on its
// own, not something to acquire by picking the other predicate here.
import { WorldState } from './state';
import { SimEvents } from './events';
import { MobProjectileId, ObjectDef, RoomDef } from './types';
import { DEFAULT_PHYSICS, PhysicsConfig } from './physics';
import { canObjectEnter, doorAt } from './queries';
import { moveObjectTo } from './casts';

/** Where a mob would step to put itself `standoff` tiles from whatever is at
 * (toX, toY) -- or null if it is already there, or has nowhere to go this step.
 *
 * Greedy and one tile at a time, deliberately: no path is planned, remembered
 * or shared. A mob closes the larger of its two gaps first (so it lines up on
 * one axis and then runs down the other), and falls back to the smaller axis
 * when that tile is taken -- which is enough to walk around a single obstacle
 * and not enough to solve a maze. That is the honest description of a Grunt,
 * and it keeps the thing a player has to model in their head small: it comes
 * straight at you unless something is in the way.
 *
 * **`standoff` is the whole of the Archer**, and it is a distance rather than a
 * second behaviour: measured in Manhattan tiles (the number of steps between the
 * two, which is the metric a grid walker actually pays), it approaches while it
 * is further out than that, backs away while it is closer, and holds when it is
 * exactly there. `0` -- every other kind -- reduces to "always approach", since
 * a mob can never actually reach 0 while it refuses the caster's own tile.
 * Backing away reuses the same axis ordering with the step negated, so a
 * retreating Archer keeps going the way it was already going instead of jittering
 * between two axes. An Archer with its back to a wall simply holds, which reads
 * as cornered and is the correct thing to look like.
 *
 * Ties go to the horizontal, arbitrarily but *fixedly*: the simulation has no
 * randomness anywhere (a recorded take has to replay to the same world), so a
 * coin flip is not available and would not be wanted if it were.
 *
 * Pure -- mutates nothing, reads no clock. The caster's tile is refused rather
 * than walked onto: a mob stops beside you, and what standing next to one costs
 * is a question for whatever hazard mechanism eventually answers it, not
 * something to smuggle in as an overlap. Doorways are refused for the same
 * reason every other object mover refuses them: crossing one is a room
 * transition, not a step. */
export function mobDestination(
  room: RoomDef,
  mob: ObjectDef,
  toX: number,
  toY: number,
  physics: PhysicsConfig = DEFAULT_PHYSICS,
  standoff = 0,
): { x: number; y: number } | null {
  const dx = toX - mob.x;
  const dy = toY - mob.y;
  const gap = Math.abs(dx) + Math.abs(dy);
  if (gap === 0 || gap === standoff) return null;
  const toward = gap > standoff ? 1 : -1;

  const horizontal = { x: mob.x + Math.sign(dx) * toward, y: mob.y };
  const vertical = { x: mob.x, y: mob.y + Math.sign(dy) * toward };
  const wanted = Math.abs(dx) >= Math.abs(dy) ? [horizontal, vertical] : [vertical, horizontal];

  for (const step of wanted) {
    if (step.x === mob.x && step.y === mob.y) continue; // already aligned on that axis
    if (step.x === toX && step.y === toY) continue; // never onto the caster
    if (doorAt(room, step.x, step.y)) continue;
    if (!canObjectEnter(room, step.x, step.y, physics)) continue;
    return step;
  }
  return null;
}

/** How each projectile is drawn: which `project-fx` texture, and whether the
 * sprite has a nose that has to point where it is going.
 *
 * Four of the five spells are the exact texture the cast they depict already
 * uses (see `applyProjectEffect`), which is the point: a mob's fire is the
 * *same* fire, so the day these become real casts nothing about how they look
 * changes. `decay` is the exception and worth saying out loud -- `Project`·
 * `Entropy` has no world effect at all yet, so there is no existing look to
 * borrow and `fx-decay` is a new one, drawn green, waiting for the cast it
 * belongs to. The arrow is the only `oriented` one: a flame or a frost bloom
 * reads the same whichever way it was thrown, and an arrow pointing backwards
 * reads as a mistake. */
const PROJECTILE_FX: Record<MobProjectileId, { texture: string; oriented?: boolean }> = {
  fire: { texture: 'fx-flame' },
  ice: { texture: 'fx-frost' },
  charge: { texture: 'fx-bolt' },
  decay: { texture: 'fx-decay' },
  force: { texture: 'fx-shock' },
  arrow: { texture: 'fx-arrow', oriented: true },
};

/** The five spells, in a fixed order -- what an Apprentice picks from, and
 * deliberately not simply "every projectile there is": an Apprentice is a mage
 * who has not chosen an element, not an archer who forgot their bow. */
export const APPRENTICE_SPELLS: MobProjectileId[] = ['fire', 'ice', 'charge', 'decay', 'force'];

/** What this cast throws: the mob's own projectile, or -- for an Apprentice,
 * which has none -- one derived from its id and how many it has thrown.
 *
 * **"Random" here cannot mean `Math.random`.** Nothing in this simulation reads
 * a clock or a random source, because a recorded take has to replay to exactly
 * the world it recorded (see `sim/replay.ts`), and one apprentice throwing fire
 * on the recording and ice on the replay would make every take useless. So this
 * is a hash of two things that already exist and already differ: which mob it is
 * (so two apprentices in a room do not chant in unison) and which cast this is
 * (so one apprentice does not throw the same thing forever). FNV-1a, because it
 * is four lines and mixes a short string well enough that consecutive casts do
 * not walk the list in order -- which is all "random" has to mean here: a player
 * cannot predict the next one, and the world can.
 *
 * The consequence worth knowing: an apprentice's sequence is a property of its
 * id, so two apprentices with the same id in different rooms throw the same
 * spells, and renaming one re-rolls it forever. Both are fine, and both are the
 * price of being reproducible. */
export function pickProjectile(mob: ObjectDef): MobProjectileId {
  const authored = mob.mob?.projectile;
  if (authored) return authored;
  const seed = `${mob.id}:${mob.mob?.casts ?? 0}`;
  let hash = 0x811c9dc5;
  for (let i = 0; i < seed.length; i++) {
    hash ^= seed.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193) >>> 0;
  }
  return APPRENTICE_SPELLS[hash % APPRENTICE_SPELLS.length];
}

/** The projection a casting mob throws this tick, as the renderer's own
 * `project-fx` event -- and nothing else. No concept is drawn, nothing is spent,
 * and standing in it costs exactly nothing; see this file's header.
 *
 * Thrown down whichever axis the caster is further along, reaching as far as they
 * actually are on that axis and no further (capped by `castRange`). So a mob
 * standing beside you throws one tile and a mob across the room throws the length
 * of the room -- the beam ends where the target is rather than always running to
 * full reach, which is what makes it read as aimed. Off-axis, it goes past them
 * on the near side, which is what "thrown in your direction" looks like when the
 * thing throwing it walks on a grid. */
function castAt(mob: ObjectDef, toX: number, toY: number, range: number, out: SimEvents): void {
  const dx = toX - mob.x;
  const dy = toY - mob.y;
  if (dx === 0 && dy === 0) return;
  const horizontal = Math.abs(dx) >= Math.abs(dy);
  const offset = horizontal ? { x: Math.sign(dx), y: 0 } : { x: 0, y: Math.sign(dy) };
  const fx = PROJECTILE_FX[pickProjectile(mob)];
  out.push({
    kind: 'project-fx',
    texture: fx.texture,
    radial: false,
    range: Math.min(horizontal ? Math.abs(dx) : Math.abs(dy), range),
    offset,
    oriented: fx.oriented,
    // The one thing a mob's projection needs that a caster's never did: it comes
    // from somewhere other than the caster's own feet.
    origin: { x: mob.x, y: mob.y },
  });
}

/** What the mobs in a room actually did over one advance. Two counts rather
 * than one because they are two independent clocks, and because the renderer's
 * own "is this batch worth drawing" gate has to see either of them: a Drone Mage
 * that stood still and threw a spell moved nothing at all. */
export interface MobActions {
  steps: number;
  casts: number;
}

/** Advances every mob in the room the caster is standing in by `dtMs`, and
 * returns what they did.
 *
 * Only that room, matching the rule the rest of `tick` already follows: an
 * unvisited room does not decay, does not conduct and does not thaw, and a mob
 * pacing an empty room the player has never seen is the same claim about
 * simulating things nobody is looking at. It also means "chasing the player" and
 * "in the same room as the player" are one condition rather than two, so there
 * is no aggro state to author, forget, or reset.
 *
 * Loops rather than stepping at most once, the same way `tick`'s own two rules
 * do: `advanceMobs(world, 8000)` is four Grunt steps, each re-aimed at where the
 * caster is *now*, so a large skipped interval behaves like the many small ones
 * it stands for. The remainder is carried per mob, so nothing drifts.
 *
 * A mob currently held by a `bind` link does not step at all. Its movement is
 * already entirely dictated by the locus it follows (see `queries.canPush`'s own
 * comment, which refuses an independent shove for exactly the same reason), so
 * a step of its own would double-count against the link -- and a Grunt bound to
 * the caster following them about, or Reverse-bound and driven away, is a real
 * answer to one rather than a bug. **It still casts**: a bound mage has been
 * taken off its feet, not silenced, and the two are separate clocks precisely so
 * that answering one of them is not answering both.
 *
 * Casting runs on its own interval alongside stepping, in the same loop and off
 * the same delta. Deliberately not run to a fixpoint against the movement:
 * within one call a mob does all its stepping and then all its casting, so a
 * skipped ten seconds throws its spells from where the mob ended up rather than
 * from each intermediate tile. That is a real (and, at any honest frame rate,
 * invisible) simplification, and the alternative -- interleaving two clocks
 * exactly -- is a scheduler, which is a great deal of machinery for a difference
 * only a test that skips ten seconds at once can see. */
export function advanceMobs(world: WorldState, dtMs: number, out: SimEvents): MobActions {
  const room = world.rooms[world.player.roomId];
  if (!room || dtMs <= 0) return { steps: 0, casts: 0 };

  const done: MobActions = { steps: 0, casts: 0 };
  // A snapshot, because `moveObjectTo` settles the world and a settle may add
  // or remove objects (a vine segment, an altar's activation) -- the same
  // reason `applyRoomDecay` iterates a copy.
  for (const mob of room.objects.filter((o) => o.mob !== undefined)) {
    const clock = mob.mob!;
    const kind = world.physics.mob[clock.variant ?? 'grunt'];
    if (!kind) continue;

    // A kind with no row in the physics table, or one tuned to zero, would
    // otherwise spin here forever. Standing still is the right failure: it is
    // visible in play and harmless, where a hang is neither. The same guard
    // covers `castMs`, whose *absence* is the ordinary way to say "this kind
    // throws nothing" rather than a misconfiguration.
    if (kind.stepMs > 0 && !mob.bind) {
      clock.sinceStepMs = (clock.sinceStepMs ?? 0) + dtMs;
      while (clock.sinceStepMs >= kind.stepMs) {
        clock.sinceStepMs -= kind.stepMs;
        const dest = mobDestination(room, mob, world.player.x, world.player.y, world.physics, kind.standoff ?? 0);
        // A blocked step still spends the interval -- the mob tried and got
        // nowhere, rather than banking the attempt and lurching two tiles the
        // moment the way clears. So does a step a standoff mob did not want to
        // take: holding its distance is a choice it makes every interval.
        if (!dest) continue;
        moveObjectTo(world, mob, dest.x, dest.y, out);
        done.steps++;
      }
    }

    if (kind.castMs !== undefined && kind.castMs > 0) {
      clock.sinceCastMs = (clock.sinceCastMs ?? 0) + dtMs;
      while (clock.sinceCastMs >= kind.castMs) {
        clock.sinceCastMs -= kind.castMs;
        castAt(mob, world.player.x, world.player.y, kind.castRange ?? 0, out);
        // Counted whether or not anything was drawn, because it is the count an
        // Apprentice's next choice is derived from: a cast that produced no beam
        // (the caster is standing on it, which nothing can currently arrange)
        // still happened, and skipping it would make the sequence depend on
        // geometry.
        clock.casts = (clock.casts ?? 0) + 1;
        done.casts++;
      }
    }
  }
  return done;
}
